import { RepwiseStore } from '@repwise/sync';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { isUpdateReady, markUpdateReady, onUpdateReady } from './lib/update.ts';
import { App } from './app/App.tsx';
import { isStandalone } from './lib/hooks.ts';
import { createRepo, type Repo } from './lib/repo.ts';
import { applyTheme } from './lib/theme.ts';
import { APP_BUILD, captureErrors } from './lib/feedback.ts';
import { startDiagnostics } from './diag/monitor.ts';
import { diag } from './diag/log.ts';
import { captureInstallPrompt } from './lib/platform.ts';
import './styles/app.css';

startDiagnostics(APP_BUILD); // ADR-0024: before anything else, so a hang at start is caught too
applyTheme();
captureInstallPrompt(); // Android/desktop Chromium: keep the install prompt for our button
captureErrors(); // the last few uncaught errors, offered as feedback context

const updateSW = registerSW({
  onNeedRefresh: () => (diag('app', 'update_ready'), markUpdateReady()),
  onOfflineReady: () => diag('app', 'offline_ready'),
  onRegisterError: (e: unknown) => diag('app', 'sw_register_failed', { message: e instanceof Error ? e.message : String(e) }, 'error'),
});

function Root({ repo }: { repo: Repo }) {
  const [updateReady, setUpdateReady] = useState(isUpdateReady);
  useEffect(() => onUpdateReady(setUpdateReady), []);
  return (
    <BrowserRouter>
      <App repo={repo} updateReady={updateReady} applyUpdate={() => void updateSW(true)} />
    </BrowserRouter>
  );
}

/** US-18: private browsing or blocked site data leaves no IndexedDB. Say so plainly instead of failing blank. */
function StorageUnavailable() {
  return (
    <main className="min-h-dvh p-5 max-w-[480px] mx-auto flex flex-col justify-center">
      {/* ER01 `_storage` final (Wave 2.4 batch 4) */}
      <div className="bg-surface-raised rounded-[20px] p-6 space-y-3">
        <span className="min-w-11 min-h-11 rounded-[12px] bg-surface-sunken grid place-items-center text-sync-warn font-bold" aria-hidden="true">
          !
        </span>
        <h1 className="text-title">Repwise can't save on this device</h1>
        <p>This browser isn't letting Repwise store data. That usually means a private window, or site data being blocked in settings.</p>
        <p className="text-text-muted">Open Repwise in a normal window, or allow site data for this site, then reload. Nothing has been lost: workouts already synced to your account are safe.</p>
        <button type="button" className="w-full min-h-12 px-4 rounded-[14px] bg-surface-sunken" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    </main>
  );
}

/**
 * Shown at once while the local database opens. If that stalls (e.g. Repwise open in another tab during an
 * update, or storage stuck), say so instead of a blank screen, and log it (phone test, 14 Oct 2026).
 */
function Starting({ slow }: { slow: boolean }) {
  if (!slow) return <main className="min-h-dvh grid place-items-center text-text-muted">Starting Repwise…</main>;
  return (
    <main className="min-h-dvh p-5 max-w-[480px] mx-auto flex flex-col justify-center" role="alert">
      <div className="bg-surface-raised rounded-[20px] p-6 space-y-3">
        <h1 className="text-title">Repwise is taking longer than usual to open</h1>
        <p className="text-text-muted">Your workouts are safe on this phone. If Repwise is open in another tab or window, close that one, then reload.</p>
        <button type="button" className="w-full min-h-12 px-4 rounded-[14px] bg-surface-sunken" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </main>
  );
}

void (async () => {
  const root = createRoot(document.getElementById('root')!);
  root.render(<Starting slow={false} />);
  const t0 = performance.now();
  const slow = setTimeout(() => (diag('app', 'db_open_slow', { ms: Math.round(performance.now() - t0) }, 'warn'), root.render(<Starting slow />)), 6000);
  let repo: Repo;
  try {
    repo = await createRepo();
  } catch {
    clearTimeout(slow);
    root.render(<StorageUnavailable />);
    return;
  }
  clearTimeout(slow);
  const ms = performance.now() - t0;
  if (ms >= 1000) diag('app', 'db_open', { ms: Math.round(ms) }, 'warn');
  // iOS evicts storage of web apps; ask once, in the installed app, where it can matter (research/03).
  if (isStandalone()) void RepwiseStore.requestPersistence();
  root.render(
    <StrictMode>
      <Root repo={repo} />
    </StrictMode>,
  );
})();
